/**
 *
 * OrderPatientSearch
 *
 */

import {useEffect, useState} from "react";
import * as PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";
import {
  makeSelectOrderPatientError,
  makeSelectOrderPatientLoading,
  makeSelectOrderPatientPatients,
  makeSelectOrderPatientPatientsTotal,
} from "./selectors";
import reducer from "./reducer";
import {createStructuredSelector} from "reselect";
import saga from "./saga";
import messages from "./messages";
import {getPatients} from "containers/OrderPatientSearch/actions";
import {makeStyles} from "@material-ui/core/styles";
import CustomInputComponent from "components/CustomInputComponent/CustomInputComponent";
import {Add as AddIcon} from "@material-ui/icons";
import {Button, Table, TableBody, TableCell, TableHead, TableRow,} from "@material-ui/core";
import GlobalMessages from "messages/messages";
import {useDebounce} from "use-debounce";
import clsx from "clsx";
import ArrowIcon from "components/CalendarView/ArrowIcon";
import {connect} from "react-redux";
import {compose} from "redux";
import injectSaga from "utils/injectSaga";
import injectReducer from "utils/injectReducer";

const useStyles = makeStyles(theme => ({
  newPatientButton: {
    textAlign: "left",
    fontSize: "15px",
    fontWeight: "bold",
  },
  wrapper: {
    display: "contents",
  },
  searchBox: {
    minWidth: "100%",
    [theme.breakpoints.down("xs")]: {
      minWidth: "0",
    },
    position: "relative",
  },
  buttonContainer: {
    margin: theme.spacing(1),
  },
  roundedButton: {
    borderRadius: "25px",
  },
  resultContainer: {
    border: "1px solid #464646",
    paddingTop: theme.spacing(3),
    marginTop: theme.spacing(-2.5),
    zIndex: -1,
    borderBottomLeftRadius: "15px",
    borderBottomRightRadius: "15px",
    borderTop: "none",
  },
  searchInput: {
    zIndex: 2,
    borderColor: "#464646",

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#464646",
    },
  },
  clickable: {
    cursor: "pointer",
  },
  noBorderLast: {
    "&:last-child": {
      "& td": {
        borderBottom: "0",
      },
    },
  },
  noResults: {
    width: "100%",
    minHeight: "100px",
    textAlign: "center",
  },
  arrowContainer: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    display: "flex",
    flexDirection: "row"
  },
  arrowButton: {
    outline: "none",
    cursor: "pointer",
  },
}));

function OrderPatientSearch({
  patients,
  onPatientClick,
  onNewClick,
  onGetPatients,
  onClickExpand,
  total
}) {
  const classes = useStyles();

  const [filter, setFilter] = useState("");

  const [debouncedFilter] = useDebounce(filter, 400);

  useEffect(
    () => {
      if (debouncedFilter.length > 2) {
        onGetPatients(debouncedFilter);
      }
    },
    [debouncedFilter, onGetPatients],
  );

  return (
    <div className={classes.searchBox}>
      <CustomInputComponent
        onChange={e => setFilter(e.target.value)}
        value={filter}
        fullWidth
        className={classes.searchInput}
        label={<FormattedMessage {...messages.searchPatient} />}
      />
      {debouncedFilter.length > 2 && (
        <div className={classes.resultContainer}>
          <div className={classes.buttonContainer}>
            <Button
              variant="outlined"
              fullWidth
              endIcon={<AddIcon />}
              className={classes.roundedButton}
              onClick={() => {
                onNewClick(filter);
              }}
            >
              <FormattedMessage {...GlobalMessages.patientAdd} />
            </Button>
          </div>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <FormattedMessage {...GlobalMessages.patientId} />
                </TableCell>
                <TableCell>
                  <FormattedMessage {...GlobalMessages.internal_ref} />
                </TableCell>
                <TableCell>
                  <FormattedMessage {...GlobalMessages.caseNr} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patients.length === 0 && (
                <TableRow>
                  <TableCell style={{ borderBottom: "0" }} colSpan={3}>
                    <span>
                      <FormattedMessage {...GlobalMessages.no_search_results} />
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {patients.slice(0, 5).map(patient => (
                <TableRow
                  hover
                  onClick={() => onPatientClick(patient)}
                  key={patient.id}
                  className={clsx(classes.clickable, classes.noBorderLast)}
                >
                  <TableCell>{patient.name}</TableCell>
                  <TableCell>{patient.internalRef}</TableCell>
                  <TableCell>{patient.caseId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {onClickExpand && (
        <div className={classes.arrowContainer}>
          <button
            type="button"
            onClick={() => {
              onClickExpand(filter);
            }}
            className={classes.arrowButton}
          >
            <ArrowIcon direction="down" />
          </button>
          {total > 3 && debouncedFilter.length > 2  && (
              <div data-cy="badgePatients">
                {total - 3} <FormattedMessage {...GlobalMessages.extra_options} />
              </div>
          )}
        </div>
      )}
    </div>
  );
}

OrderPatientSearch.propTypes = {
  // Redux & others
  patients: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
  onGetPatients: PropTypes.func.isRequired,

  // User given
  onPatientClick: PropTypes.func.isRequired,
  onNewClick: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  patients: makeSelectOrderPatientPatients(),
  loading: makeSelectOrderPatientLoading(),
  error: makeSelectOrderPatientError(),
  total: makeSelectOrderPatientPatientsTotal(),
});

function mapDispatchToProps(dispatch) {
  return {
    onGetPatients: filter => dispatch(getPatients(filter)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

const withReducer = injectReducer({ key: "orderPatientSearch", reducer });
const withSaga = injectSaga({ key: "orderPatientSearch", saga });

export default compose(
  withReducer,
  withSaga,
  withConnect,
)(OrderPatientSearch);
